import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTimes, faEdit } from "@fortawesome/free-solid-svg-icons";
import EditableCell from "./EditableCell";

// DataCell component that handles its own editing state
const DataCell = ({
  type = "text",
  value,
  onChange,
  onSave,
  disabled = false,
  options = [],
  fieldName = "",
  id,
  parentEditingId,
  setParentEditingId,
  readOnlyFormatter = (val) => val || "-",
}) => {
  const [localEditing, setLocalEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  // Update tempValue when value prop changes
  useEffect(() => {
    setTempValue(value);
  }, [value]);

  // Determine if cell should be in edit mode
  const isEditing = parentEditingId === id || localEditing;

  // Handle starting edit mode via double-click
  const handleStartEditing = () => {
    if (disabled || isEditing) return;

    // If there's already a cell being edited, save that one first
    if (parentEditingId && parentEditingId !== id) {
      // This would trigger the save action on the currently edited cell
      setParentEditingId(null);
    }

    setTempValue(value);
    setLocalEditing(true);
    setParentEditingId(id);
  };

  // Handle change in cell value
  const handleChange = (newValue) => {
    setTempValue(newValue);
  };

  // Handle save action
  const handleSave = () => {
    onChange(tempValue);
    if (onSave) onSave(id, tempValue);
    setLocalEditing(false);
    setParentEditingId(null);
  };

  // Handle cancel action
  const handleCancel = () => {
    setTempValue(value);
    setLocalEditing(false);
    setParentEditingId(null);
  };

  // If displaying in read-only mode (not editing)
  if (!isEditing) {
    return (
      <div className="relative">
        <div
          className="px-4 py-2 transition-colors duration-150 hover:bg-blue-50 rounded cursor-pointer"
          onDoubleClick={handleStartEditing}
          title={`Double-click to edit ${fieldName}`}
        >
          {type === "date"
            ? readOnlyFormatter(value)
            : value !== undefined && value !== null
            ? String(value)
            : "-"}
        </div>
      </div>
    );
  }

  // If in edit mode
  return (
    <div className="relative px-4 py-2 bg-blue-50 -m-px rounded">
      <div className="flex space-x-2">
        <div className="flex-1">
          <EditableCell
            type={type}
            value={tempValue}
            onChange={handleChange}
            onSave={handleSave}
            onCancel={handleCancel}
            options={options}
            isEditing={true}
            fieldName={fieldName}
            showShortcutsHelp={true}
          />
        </div>
        <div className="flex space-x-1 items-start">
          <button
            onClick={handleSave}
            className="text-green-600 hover:text-green-900 p-1"
            title="Save changes"
          >
            <FontAwesomeIcon icon={faSave} />
          </button>
          <button
            onClick={handleCancel}
            className="text-red-600 hover:text-red-900 p-1"
            title="Cancel"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {/* Show keyboard shortcuts hint */}
      <div className="text-xs text-gray-500 mt-1">
        Press <span className="px-1 bg-gray-200 rounded">Enter</span> to save,{" "}
        <span className="px-1 bg-gray-200 rounded">Esc</span> to cancel
      </div>
    </div>
  );
};

export default DataCell;
